/**
 * IndexedDB-backed store for upload retries.
 *
 * When the initial /api/recordings/upload bg task fails, the server flips
 * the row to `failed`. The original audio bytes only exist on the
 * recording device, so we persist them locally keyed by the row id and
 * surface a retry action. Entries TTL out after 7 days (matches the
 * server-side `failed`-row hard-delete window).
 */

const DB_NAME = "carmen";
const DB_VERSION = 1;
const STORE = "pendingUploads";
const MAX_ENTRIES = 5;
const DEFAULT_TTL_DAYS = 7;

export type PendingUpload = {
  recording_id: string;
  blob: Blob;
  mime_type: string;
  source: "live" | "ambient";
  recorded_at: string;
  duration_seconds: number | null;
  display_name: string;
  created_at: number;
};

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "recording_id" });
        store.createIndex("created_at", "created_at", { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB open failed"));
  });
  return dbPromise;
}

function txStore(mode: IDBTransactionMode): Promise<IDBObjectStore> {
  return openDB().then((db) => db.transaction(STORE, mode).objectStore(STORE));
}

function reqToPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function savePending(entry: PendingUpload): Promise<void> {
  // Enforce a soft cap: oldest entries get evicted first.
  const all = await listPending();
  if (all.length >= MAX_ENTRIES) {
    const sorted = [...all].sort((a, b) => a.created_at - b.created_at);
    const overflow = sorted.slice(0, all.length - MAX_ENTRIES + 1);
    await Promise.all(overflow.map((e) => deletePending(e.recording_id)));
  }
  const store = await txStore("readwrite");
  await reqToPromise(store.put(entry));
}

export async function getPending(recordingId: string): Promise<PendingUpload | null> {
  const store = await txStore("readonly");
  const result = await reqToPromise(store.get(recordingId));
  return (result as PendingUpload | undefined) ?? null;
}

export async function listPending(): Promise<PendingUpload[]> {
  const store = await txStore("readonly");
  const result = await reqToPromise(store.getAll());
  return (result as PendingUpload[]) ?? [];
}

export async function deletePending(recordingId: string): Promise<void> {
  const store = await txStore("readwrite");
  await reqToPromise(store.delete(recordingId));
}

export async function clearPendingUploads(): Promise<void> {
  const store = await txStore("readwrite");
  await reqToPromise(store.clear());
}

export async function clearOlderThan(days = DEFAULT_TTL_DAYS): Promise<number> {
  const cutoff = Date.now() - days * 86_400_000;
  const all = await listPending();
  const stale = all.filter((e) => e.created_at < cutoff);
  await Promise.all(stale.map((e) => deletePending(e.recording_id)));
  return stale.length;
}
